import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "react-bootstrap/Button";
import dateFormat from "dateformat";
import { promoPrice as promoPriceRules } from "../../utils/rules";
import "./promotion.scss";
import DiscountIcon from "../../images/Priva_Discount_Promo_Icon.png";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useToasts } from "react-toast-notifications";
import currency from "currency.js";
import PriceV2 from "../../common/PriceV2";
import moment from "moment";
import CloseButton from "../../common/CloseButton";
import { useHistory } from "react-router";
import { calculatePercentage, toFixedTrunc } from "../../utils/helper";
import { connect } from "react-redux";
import DiscountLink from "./discount-link";
import { Divider, useMediaQuery } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import DefaultPUWidget from "../../Widgets/PU/Default";
import DefaultWidget from "../../Widgets/SU/Default";
import { ROLES } from "../../utils/constants";
import { useDispatch } from "react-redux";
import { refetchPromotion } from "../../../redux/actions/promotions";
import TrialLinks from "./TrialLinks";

const Promotion = ({ user }) => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery("(max-width: 899px)");
    const { addToast } = useToasts();

    const history = useHistory();

    const userInformation = JSON.parse(localStorage.getItem("user"));

    useEffect(() => {
        getPromotion();

        return () => stopTime();
    }, []);

    const [userPromotion, setUserPromotion] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [promoInterval, setPromoInterval] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [percentShow, setPercentShow] = useState(0);
    const [amount, setAmount] = useState(0);

    const formik = useFormik({
        initialValues: {
            promo_price: 0.5,
            promo_duration: 0,
            promo_limit: 0,
            promo_message: "",
            promo_start: "",
        },
        validationSchema: Yup.object({
            promo_price: Yup.lazy((value) => {
                return Yup.number()
                    .test("is-decimal", "Invalid promotion price", (value) =>
                        (value + "").match(/^[0-9]\d*(\.\d+)?$/)
                    )
                    .typeError("Invalid promotion price")
                    .moreThan(
                        promoPriceRules.minAmount,
                        `Enter at least ${currency(promoPriceRules.minAmount)
                            .add(0.01)
                            .format()}`
                    )
                    .lessThan(
                        userPromotion.subscription_price,
                        `Please enter a value less than ${currency(
                            userPromotion.subscription_price
                        ).format()}`
                    );
            }),
            promo_duration: Yup.string().required(
                "Promotion duration is required"
            ),
            promo_limit: Yup.number()
                .typeError("Invalid promotion limit")
                .required("Promotion limit is required"),
        }),
        onSubmit: (values, { setErrors }) => {
            console.log({ values });
            submitForm(values);
        },
    });

    const submitForm = async (values) => {
        setIsLoading(true);
        await axios
            .patch(`/api/promotions/${localStorage.getItem("loggedin_user")}`, {
                ...values,
            })
            .then((response) => {
                console.log({ response });
                getPromotion();
                addToast("Promotion successfully updated", {
                    appearance: "success",
                    autoDismiss: true,
                });
                dispatch(refetchPromotion(true));
            })
            .catch((error) => {
                console.log({ error });
                if (error.response.status === 422) {
                    Object.keys(error.response.data.errors).forEach((key) => {
                        setErrors({
                            [key]: error.response.data.errors[key][0],
                        });
                    });
                } else {
                    addToast("Failed to update promotion", {
                        appearance: "error",
                        autoDismiss: true,
                    });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getPromotion = async () => {
        await axios.get(`/api/promotions`).then((response) => {
            setUserPromotion({
                ...userPromotion,
                ...response.data,
            });
            let percent = calculatePercentage(
                response.data.promo_price || 0.5,
                response.data.subscription_price
            );
            setPercentShow(percent.toFixed(2).replace(".00", ""));
        });
    };

    const fillForm = (promotion) => {
        Object.keys(formik.values).forEach((key) => {
            if (promotion[key] != null) {
                if (key == "promo_start") {
                    formik.setFieldValue(key, promotion["status"], false);
                } else {
                    formik.setFieldValue(key, promotion[key], false);
                }
            }
        });
    };

    useEffect(() => {
        if (userPromotion && Object.keys(userPromotion).length > 0) {
            stopTime();
            fillForm(userPromotion);

            if (userPromotion.promo_start) {
                const promoDate = moment
                    .utc(userPromotion.promo_start)
                    .local()
                    .add(userPromotion.promo_duration, "hours");
                const interval = 1000;
                let duration = moment.duration(promoDate.diff(moment()));

                const timer = setInterval(() => {
                    duration = moment.duration(
                        duration - interval,
                        "milliseconds"
                    );
                    if (duration.asSeconds() <= 0) {
                        stopTime();
                    } else {
                        setStartDate(
                            `${
                                duration.days() > 0
                                    ? `${duration.days()} day `
                                    : ""
                            }${duration.hours()} hours ${duration.minutes()} min ${duration.seconds()} sec`
                        );
                    }
                }, 1000);

                setPromoInterval(timer);
            }
        }
    }, [userPromotion]);

    const handleStartPromotion = () => {
        formik.setFieldValue("promo_start", true, false);
        formik.submitForm();
    };

    const handleEndPromotion = () => {
        formik.setFieldValue("promo_start", false, false);
        formik.submitForm();
    };

    const stopTime = () => {
        clearInterval(promoInterval);
    };

    const handlePromoPriceChange = (e) => {
        e.preventDefault();
        let amount = e.target.value;

        formik.setFieldValue("promo_price", amount, true);
        setAmount(e.target.value);

        // formik.setFieldValue('promo_price', promoPrice, true);

        let percent = calculatePercentage(
            e.target.value,
            userPromotion.subscription_price
        );

        if (isNaN(percent)) {
            setPercentShow(0);
        } else {
            setPercentShow(percent.toFixed(2).replace(".00", ""));
        }
    };

    useEffect(() => {
        if (amount > 0) {
            let price = amount.toString().replace(/^0+/, "");
            price = price.replace(/[\.\%]/g, function (match, offset, all) {
                return match === "."
                    ? all.indexOf(".") === offset
                        ? "."
                        : ""
                    : "";
            });
            const fixedNum = toFixedTrunc(parseFloat(price), 2);
            formik.setFieldValue("promo_price", fixedNum, true);
        }
    }, [amount]);

    const handleGoToProfilePage = () => {
        history.push(`/${userInformation.login}`);
    };

    return (
        <div className="container pt-4 mb-4">
            <Grid2 container spacing={!isMobile ? 2 : 0}>
                <Grid2 md={8}>
                    <div className="promotion-subscription-container">
                        <div className="panel panel-default">
                            <div className="panel-heading refpayout promotionpanel">
                                <div className="panel-heading-promotion-left">
                                    <span>
                                        PROMOTION - Subscription Discount
                                    </span>
                                </div>

                                <CloseButton onClick={handleGoToProfilePage} />
                            </div>
                            <div className="panel-body">
                                <div className="promotion-form">
                                    <form onSubmit={formik.handleSubmit}>
                                        <div className="promotionpanel-fields">
                                            <div className="promo__status mb-2 mt-3">
                                                PROMO STATUS:{" "}
                                                {userPromotion.promo_status ? (
                                                    <span className="text-blue">
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="text-blue">
                                                        Not Active
                                                    </span>
                                                )}
                                            </div>
                                            <p>
                                                Set a discounted subscription
                                                price that applies to new
                                                subscribers only for the first
                                                month only.
                                            </p>

                                            <div
                                                className={`promotion-field-group${
                                                    formik.errors.promo_price &&
                                                    formik.touched.promo_price
                                                        ? " has-error"
                                                        : ""
                                                }`}
                                            >
                                                <label>
                                                    <b>Promo Price</b> (Minimum
                                                    price is $0.50)
                                                </label>
                                                <div className="promotion-field-input-description">
                                                    <PriceV2
                                                        id="promo_price"
                                                        name="promo_price"
                                                        placeholder=""
                                                        className={`ppv-price ${
                                                            formik.errors
                                                                .promo_price &&
                                                            formik.touched
                                                                .promo_price
                                                                ? " is-invalid"
                                                                : ""
                                                        }`}
                                                        onChange={
                                                            handlePromoPriceChange
                                                        }
                                                        onBlur={
                                                            formik.handleBlur
                                                        }
                                                        value={
                                                            formik.values
                                                                .promo_price
                                                        }
                                                        disabled={
                                                            userPromotion.promo_status
                                                        }
                                                        error={
                                                            formik.errors
                                                                .promo_price &&
                                                            formik.touched
                                                                .promo_price
                                                        }
                                                        inputMode="decimal"
                                                    />
                                                    <div className="promotion-field-description ml-2">
                                                        <p>
                                                            The discount that
                                                            people will see on
                                                            your profile for
                                                            this promo is
                                                            <span className="highlight">
                                                                {" "}
                                                                {percentShow}%
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                                {formik.errors.promo_price &&
                                                formik.touched.promo_price ? (
                                                    <div className="invalid-feedback">
                                                        {
                                                            formik.errors
                                                                .promo_price
                                                        }
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="promotion-field-group">
                                                <label>
                                                    <b>Promo Duration</b>
                                                </label>
                                                <select
                                                    name="promo_duration"
                                                    id="promo_duration"
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    value={
                                                        formik.values
                                                            .promo_duration
                                                    }
                                                    disabled={
                                                        userPromotion.promo_status
                                                    }
                                                >
                                                    <option value="1">
                                                        1 hour
                                                    </option>
                                                    <option value="8">
                                                        8 hour
                                                    </option>
                                                    <option value="24">
                                                        24 hour
                                                    </option>
                                                    <option value="48">
                                                        48 hour
                                                    </option>
                                                    <option value="0">
                                                        No Limit
                                                    </option>
                                                </select>
                                                {formik.errors.promo_duration &&
                                                formik.touched
                                                    .promo_duration ? (
                                                    <div className="invalid-feedback">
                                                        {
                                                            formik.errors
                                                                .promo_duration
                                                        }
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="promotion-field-group">
                                                <label>
                                                    <b>Promo Limit</b> (New
                                                    Subscribers)
                                                </label>
                                                <select
                                                    name="promo_limit"
                                                    id="promo_limit"
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    value={
                                                        formik.values
                                                            .promo_limit
                                                    }
                                                    disabled={
                                                        userPromotion.promo_status
                                                    }
                                                >
                                                    <option value="0">
                                                        Unlimited
                                                    </option>
                                                    <option value="1">1</option>
                                                    <option value="5">5</option>
                                                    <option value="10">
                                                        10
                                                    </option>
                                                    <option value="50">
                                                        50
                                                    </option>
                                                    <option value="100">
                                                        100
                                                    </option>
                                                    <option value="150">
                                                        150
                                                    </option>
                                                </select>
                                                {formik.errors.promo_limit &&
                                                formik.touched.promo_limit ? (
                                                    <div className="invalid-feedback">
                                                        {
                                                            formik.errors
                                                                .promo_limit
                                                        }
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="promotion-field-group">
                                                <label>
                                                    <b>Promo Message</b>
                                                </label>
                                                <textarea
                                                    rows="3"
                                                    name="promo_message"
                                                    id="promo_message"
                                                    placeholder="Type caption for your promotion…"
                                                    className={`form-control ${
                                                        formik.errors
                                                            .promo_message &&
                                                        formik.touched
                                                            .promo_message
                                                            ? " is-invalid"
                                                            : ""
                                                    }`}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    value={
                                                        formik.values
                                                            .promo_message
                                                    }
                                                    disabled={
                                                        userPromotion.promo_status
                                                    }
                                                />
                                                {formik.errors.promo_message &&
                                                formik.touched.promo_message ? (
                                                    <div className="invalid-feedback">
                                                        {
                                                            formik.errors
                                                                .promo_message
                                                        }
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="profile-form-button-container">
                                            <div className="profile-form-button promotional-btns mb-2">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={
                                                        isLoading ||
                                                        userPromotion.promo_status
                                                    }
                                                    className="btn theme-btn theme-btn-bordered"
                                                >
                                                    {isLoading
                                                        ? "Processing"
                                                        : "Save Settings"}
                                                </Button>
                                                {userPromotion.promo_status ? (
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        disabled={isLoading}
                                                        className="btn theme-btn theme-btn-bordered my-2"
                                                        onClick={
                                                            handleEndPromotion
                                                        }
                                                    >
                                                        {isLoading
                                                            ? "Processing"
                                                            : "End Promotion"}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        disabled={isLoading}
                                                        className="btn theme-btn theme-btn-bordered my-2"
                                                        onClick={
                                                            handleStartPromotion
                                                        }
                                                    >
                                                        {isLoading
                                                            ? "Processing"
                                                            : "Start Promotion"}
                                                    </Button>
                                                )}
                                            </div>
                                            {userPromotion.promo_status ? (
                                                <div className="mt-2">
                                                    {userPromotion.promo_duration ==
                                                    0 ? (
                                                        <p>
                                                            Promo ends: No Limit
                                                        </p>
                                                    ) : (
                                                        <p>
                                                            Promo ends:{" "}
                                                            {startDate}
                                                        </p>
                                                    )}

                                                    <p>
                                                        New followers:{" "}
                                                        <span className="blue_text">
                                                            {userPromotion.promo_status
                                                                ? parseInt(
                                                                      userPromotion.promo_limit_used
                                                                  )
                                                                : 0}
                                                        </span>
                                                    </p>
                                                </div>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                    </form>
                                </div>
                                <hr />
                                <DiscountLink
                                    user={userPromotion}
                                    userId={userInformation.id}
                                />
                                <hr />
                                <TrialLinks userId={userInformation.id} />
                            </div>
                        </div>
                    </div>
                </Grid2>

                {!isMobile ? (
                    <Grid2 className="pu-homepage__right" md={4}>
                        {user && user.role == ROLES.PU ? (
                            <DefaultPUWidget />
                        ) : null}
                        {user && user.role == ROLES.SU ? (
                            <DefaultWidget />
                        ) : null}
                    </Grid2>
                ) : null}
            </Grid2>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.user.user,
    };
};

export default connect(mapStateToProps)(Promotion);
